import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { 
  User, Mail, Calendar, Shield, CreditCard, 
  Clock, CheckCircle, AlertTriangle, XCircle,
  RefreshCw, ArrowRight, Eye, EyeOff, History,
  MessageCircle, ChevronDown, ChevronUp
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MyAccount() {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  
  const [account, setAccount] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Edit states
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState("");
  const [savingName, setSavingName] = useState(false);
  
  // Password change states
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  
  // UI states
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);

  useEffect(() => {
    if (token) {
      fetchAccountData();
    }
  }, [token]);

  const fetchAccountData = async () => {
    setLoading(true);
    try {
      const [accountRes, subscriptionRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/api/account`),
        axios.get(`${API}/api/account/subscription`),
        axios.get(`${API}/api/account/payments`)
      ]);
      
      setAccount(accountRes.data);
      setSubscription(subscriptionRes.data);
      setPayments(paymentsRes.data.payments || []);
      setNewName(accountRes.data.name);
    } catch (error) {
      console.error("Error fetching account data:", error);
      toast.error("Erro ao carregar dados da conta");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveName = async () => {
    if (!newName || newName.trim().length < 2) {
      toast.error("Nome deve ter pelo menos 2 caracteres");
      return;
    }
    
    setSavingName(true);
    try {
      await axios.patch(`${API}/api/account`, { name: newName.trim() });
      setAccount({ ...account, name: newName.trim() });
      setEditingName(false);
      toast.success("Nome atualizado com sucesso");
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar nome");
    } finally {
      setSavingName(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error("Digite sua senha atual");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Nova senha deve ter pelo menos 6 caracteres");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    
    setSavingPassword(true);
    try {
      await axios.post(`${API}/api/account/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      setChangingPassword(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Senha alterada com sucesso");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setSavingPassword(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <CheckCircle className="text-green-600" size={20} />;
      case "expired":
        return <AlertTriangle className="text-red-600" size={20} />;
      case "proof_submitted":
      case "awaiting_proof":
      case "pending":
        return <Clock className="text-yellow-600" size={20} />;
      case "rejected":
        return <XCircle className="text-red-600" size={20} />;
      default:
        return <Clock className="text-gray-600" size={20} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
      case "approved":
        return "bg-green-50 text-green-700 border-green-200";
      case "expired":
      case "rejected":
        return "bg-red-50 text-red-700 border-red-200";
      case "proof_submitted":
      case "awaiting_proof":
      case "pending":
        return "bg-yellow-50 text-yellow-700 border-yellow-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const handleRenew = () => {
    navigate("/select-plan", { state: { isRenewal: true } });
  };

  const handleChangePlan = () => {
    navigate("/select-plan", { state: { isChangePlan: true } });
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 md:p-10 flex items-center justify-center min-h-[50vh]">
          <div className="animate-pulse text-muted-foreground font-mono text-sm uppercase tracking-widest">
            Carregando...
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-4xl mx-auto" data-testid="my-account-page">
        <h1 className="font-serif text-3xl md:text-4xl mb-8">Minha Conta</h1>

        {/* Personal Information */}
        <Card className="p-6 mb-6 border border-gray-200 shadow-none rounded-lg" data-testid="personal-info-card">
          <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
            <User size={20} />
            Informações Pessoais
          </h2>
          
          <div className="space-y-4">
            {/* Name */}
            <div>
              <Label className="text-sm text-muted-foreground">Nome</Label>
              {editingName ? (
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="flex-1"
                    data-testid="name-input"
                  />
                  <Button 
                    onClick={handleSaveName} 
                    disabled={savingName}
                    size="sm"
                    data-testid="save-name-btn"
                  >
                    {savingName ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => { setEditingName(false); setNewName(account?.name || ""); }}
                    size="sm"
                  >
                    Cancelar
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between mt-1">
                  <span className="text-lg" data-testid="user-name">{account?.name || "—"}</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setEditingName(true)}
                    data-testid="edit-name-btn"
                  >
                    Editar
                  </Button>
                </div>
              )}
            </div>
            
            {/* Email (read-only) */}
            <div>
              <Label className="text-sm text-muted-foreground flex items-center gap-1">
                <Mail size={14} />
                E-mail
              </Label>
              <div className="flex items-center justify-between mt-1">
                <span className="text-lg" data-testid="user-email">{account?.email || "—"}</span>
                <span className="text-xs text-muted-foreground">(não editável)</span>
              </div>
            </div>
            
            {/* Created at */}
            <div>
              <Label className="text-sm text-muted-foreground flex items-center gap-1">
                <Calendar size={14} />
                Membro desde
              </Label>
              <span className="text-lg" data-testid="created-at">{formatDate(account?.created_at) || "—"}</span>
            </div>
          </div>
        </Card>

        {/* Password Change */}
        <Card className="p-6 mb-6 border border-gray-200 shadow-none rounded-lg" data-testid="security-card">
          <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
            <Shield size={20} />
            Segurança
          </h2>
          
          {changingPassword ? (
            <div className="space-y-4">
              <div>
                <Label>Senha Atual</Label>
                <div className="relative mt-1">
                  <Input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label>Nova Senha</Label>
                <div className="relative mt-1">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Mínimo 6 caracteres</p>
              </div>
              
              <div>
                <Label>Confirmar Nova Senha</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1"
                  data-testid="confirm-password-input"
                />
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleChangePassword} 
                  disabled={savingPassword}
                  data-testid="save-password-btn"
                >
                  {savingPassword ? "Alterando..." : "Alterar Senha"}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setChangingPassword(false);
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <Button 
              variant="outline" 
              onClick={() => setChangingPassword(true)}
              data-testid="change-password-btn"
            >
              Alterar Senha
            </Button>
          )}
        </Card>

        {/* Subscription Info */}
        <Card className="p-6 mb-6 border border-gray-200 shadow-none rounded-lg" data-testid="subscription-card">
          <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
            <CreditCard size={20} />
            Minha Assinatura
          </h2>
          
          {subscription && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${getStatusColor(subscription.status)}`}>
                {getStatusIcon(subscription.status)}
                <span className="font-medium" data-testid="subscription-status">
                  {subscription.status_label}
                </span>
              </div>
              
              {/* Plan Details */}
              {subscription.plan_name && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <Label className="text-sm text-muted-foreground">Plano</Label>
                    <p className="text-lg font-medium" data-testid="subscription-plan">
                      {subscription.plan_name}
                    </p>
                  </div>
                  
                  {subscription.period_name && (
                    <div>
                      <Label className="text-sm text-muted-foreground">Modalidade</Label>
                      <p className="text-lg">{subscription.period_name}</p>
                    </div>
                  )}
                  
                  {subscription.amount && (
                    <div>
                      <Label className="text-sm text-muted-foreground">Valor</Label>
                      <p className="text-lg">${subscription.amount.toFixed(2)}</p>
                    </div>
                  )}
                  
                  {subscription.activated_at && (
                    <div>
                      <Label className="text-sm text-muted-foreground">Ativado em</Label>
                      <p className="text-lg">{formatDate(subscription.activated_at)}</p>
                    </div>
                  )}
                  
                  {subscription.expires_at && (
                    <div>
                      <Label className="text-sm text-muted-foreground">Válido até</Label>
                      <p className="text-lg" data-testid="subscription-expires">
                        {formatDate(subscription.expires_at)}
                        {subscription.days_remaining !== null && subscription.days_remaining !== undefined && (
                          <span className={`ml-2 text-sm ${
                            subscription.days_remaining <= 0 
                              ? "text-red-600" 
                              : subscription.days_remaining <= 7 
                                ? "text-orange-600" 
                                : "text-green-600"
                          }`}>
                            ({subscription.days_remaining <= 0 
                              ? "Expirado" 
                              : `${subscription.days_remaining} dias restantes`})
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Guarantee Info */}
              {subscription.guarantee_eligible && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Garantia de Satisfação:</strong> Você está dentro do período de garantia de 7 dias.
                    Válido até {formatDate(subscription.guarantee_expires_at)}.
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Entre em contato com o suporte se desejar solicitar reembolso.
                  </p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4">
                {(subscription.status === "expired" || subscription.status === "active") && (
                  <Button 
                    onClick={handleRenew}
                    className="flex items-center gap-2"
                    data-testid="renew-btn"
                  >
                    <RefreshCw size={18} />
                    Renovar Assinatura
                  </Button>
                )}
                
                {subscription.status === "active" && (
                  <Button 
                    variant="outline"
                    onClick={handleChangePlan}
                    className="flex items-center gap-2"
                    data-testid="change-plan-btn"
                  >
                    <ArrowRight size={18} />
                    Alterar Plano
                  </Button>
                )}
                
                {(subscription.status === "inactive" || subscription.status === "awaiting_proof") && (
                  <Button 
                    onClick={() => navigate("/select-plan")}
                    className="flex items-center gap-2"
                  >
                    <CreditCard size={18} />
                    Escolher Plano
                  </Button>
                )}
                
                {subscription.status === "proof_submitted" && (
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/subscription")}
                    className="flex items-center gap-2"
                  >
                    <Clock size={18} />
                    Ver Status do Pagamento
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Payment History */}
        <Card className="p-6 mb-6 border border-gray-200 shadow-none rounded-lg" data-testid="payment-history-card">
          <button
            onClick={() => setShowPaymentHistory(!showPaymentHistory)}
            className="w-full flex items-center justify-between"
          >
            <h2 className="font-serif text-xl flex items-center gap-2">
              <History size={20} />
              Histórico de Pagamentos
            </h2>
            {showPaymentHistory ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          
          {showPaymentHistory && (
            <div className="mt-4 space-y-3">
              {payments.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  Nenhum pagamento registrado
                </p>
              ) : (
                payments.map((payment) => (
                  <div 
                    key={payment.id} 
                    className="p-4 bg-muted/30 rounded-lg border"
                    data-testid="payment-history-item"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className="font-medium">{payment.plan_name}</span>
                        <span className="text-muted-foreground"> • </span>
                        <span className="text-muted-foreground">{payment.period_name}</span>
                      </div>
                      <span className={`text-sm px-2 py-1 rounded ${getStatusColor(payment.status)}`}>
                        {payment.status_label}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Valor: </span>
                        <span>${payment.amount.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Método: </span>
                        <span>{payment.payment_method_name}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Solicitado: </span>
                        <span>{formatDateTime(payment.created_at)}</span>
                      </div>
                      {payment.reviewed_at && (
                        <div>
                          <span className="text-muted-foreground">Analisado: </span>
                          <span>{formatDateTime(payment.reviewed_at)}</span>
                        </div>
                      )}
                    </div>
                    
                    {payment.rejection_reason && (
                      <div className="mt-2 p-2 bg-red-50 text-red-700 text-sm rounded">
                        <strong>Motivo:</strong> {payment.rejection_reason}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </Card>

        {/* Support Contact */}
        <Card className="p-6 border border-gray-200 shadow-none rounded-lg" data-testid="support-card">
          <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
            <MessageCircle size={20} />
            Suporte
          </h2>
          
          <p className="text-muted-foreground mb-4">
            Precisa de ajuda? Entre em contato conosco pelo WhatsApp.
          </p>
          
          <a
            href="https://wa.me/17819520080?text=Olá! Preciso de ajuda com minha conta na AESTHA HUB."
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="whatsapp-support-link"
          >
            <MessageCircle size={18} />
            Falar com Suporte
          </a>
          
          <p className="text-xs text-muted-foreground mt-4">
            Para solicitações de reembolso (dentro do período de garantia), 
            cancelamento ou dúvidas sobre sua assinatura.
          </p>
        </Card>
      </div>
    </Layout>
  );
}
